import { ApiError, apiRequest } from '../api/client.js';
import {
  deletePending,
  listPending,
  putPending,
  type PendingKind,
  type PendingWrite,
} from './db.js';

/**
 * The offline write queue.
 *
 * Only writes the user would plausibly make away from signal go through here —
 * logging food, water, a weigh-in, a workout. Everything else (editing goals,
 * creating a token, deleting an entry) stays online-only on purpose: those are
 * deliberate, rare, and a user who makes one is at a screen where "that did not
 * save" is a useful answer.
 *
 * Delivery is at-least-once, which is the only thing a queue can promise. The
 * server turns it into exactly-once *effect* by keying each write on the id
 * generated here — see `clientRequestIdSchema` in the shared package.
 */

export interface QueuedResult<T> {
  /** Present when the write reached the server. */
  data: T | null;
  /** True when it went into the queue instead. */
  queued: boolean;
}

type Listener = (pending: PendingWrite[]) => void;

const listeners = new Set<Listener>();
let flushInFlight: Promise<void> | null = null;

/** Whether the preference is on. Set once by the provider at startup. */
let enabled = false;

export function setQueueEnabled(value: boolean): void {
  enabled = value;
}

export function isQueueEnabled(): boolean {
  return enabled;
}

export function subscribeToQueue(listener: Listener): () => void {
  listeners.add(listener);
  void notify();
  return () => listeners.delete(listener);
}

async function notify(): Promise<void> {
  const pending = await listPending().catch(() => []);
  for (const listener of listeners) listener(pending);
}

/** URL-safe, collision-proof, and readable enough to grep for in a log. */
function newRequestId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID().replace(/-/g, '');
  }
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
}

/**
 * A failure that says nothing about whether the request was *understood*.
 *
 * `fetch` rejects with a TypeError for a dead network, DNS, TLS and a blocked
 * request alike. A 5xx is different — the server answered — but it is equally
 * worth retrying later, and equally not the user's problem right now.
 */
function isTransport(error: unknown): boolean {
  if (error instanceof ApiError) return error.status >= 500 || error.status === 429;
  return error instanceof TypeError || !navigator.onLine;
}

export interface QueueOptions {
  kind: PendingKind;
  path: string;
  /** Shown in the pending list, e.g. "250 ml Wasser". */
  label: string;
}

/**
 * Sends a write, or keeps it for later if that is impossible.
 *
 * The id is generated *before* the attempt and reused by every retry, so a
 * request whose response was lost in flight is recognised by the server rather
 * than applied twice. Generating it at retry time would defeat the entire
 * mechanism, which is why it lives here and not in the flush loop.
 */
export async function submitWrite<T>(
  body: Record<string, unknown>,
  options: QueueOptions,
): Promise<QueuedResult<T>> {
  const id = newRequestId();
  const payload = { ...body, clientRequestId: id };

  if (!enabled) {
    return { data: await apiRequest<T>(options.path, { body: payload }), queued: false };
  }

  // Offline is not an error worth attempting: skip straight to the queue so the
  // UI does not sit on a fetch that will time out.
  if (!navigator.onLine) {
    await enqueue(id, payload, options, 'Offline');
    return { data: null, queued: true };
  }

  try {
    return { data: await apiRequest<T>(options.path, { body: payload }), queued: false };
  } catch (error) {
    if (!isTransport(error)) throw error;
    await enqueue(id, payload, options, describe(error));
    return { data: null, queued: true };
  }
}

async function enqueue(
  id: string,
  body: unknown,
  options: QueueOptions,
  lastError: string,
): Promise<void> {
  await putPending({
    id,
    kind: options.kind,
    path: options.path,
    body,
    createdAt: Date.now(),
    attempts: 0,
    lastError,
    label: options.label,
  });
  await notify();
}

/** Attempts after which a write stops being retried automatically. */
const MAX_ATTEMPTS = 8;

/**
 * Sends everything waiting, oldest first.
 *
 * Sequential rather than parallel, and it stops at the first transport failure.
 * Both are deliberate: entries were made in an order the user remembers, and
 * once one request cannot reach the server the next twenty will not either —
 * firing them anyway just drains the battery and muddies which one broke.
 *
 * A write the server *refused* is different: the connection is fine, so the
 * loop carries on past it rather than letting one bad entry block the rest.
 */
export async function flushQueue(): Promise<void> {
  if (!enabled) return;
  flushInFlight ??= drain().finally(() => {
    flushInFlight = null;
  });
  return flushInFlight;
}

async function drain(): Promise<void> {
  const pending = await listPending().catch(() => []);

  for (const write of pending) {
    if (!navigator.onLine) break;

    // Already given up on. It stays in the list so the user can see it and
    // decide, but re-sending something the server has refused eight times is
    // just a request-per-minute for as long as the app is open.
    if (isStuck(write)) continue;

    try {
      await apiRequest(write.path, { body: write.body });
      await deletePending(write.id);
    } catch (error) {
      if (isTransport(error)) {
        await putPending({
          ...write,
          attempts: write.attempts + 1,
          lastError: describe(error),
        });
        break;
      }

      // The server understood and refused — a 400, a 403. Replaying it will
      // never work, so it stays put with the reason attached rather than
      // looping forever or being thrown away behind the user's back.
      await putPending({
        ...write,
        attempts: write.attempts + 1,
        lastError: describe(error),
      });
    }
  }

  await notify();
}

/** Drops one entry the user has decided to abandon. */
export async function discardPending(id: string): Promise<void> {
  await deletePending(id);
  await notify();
}

export function isStuck(write: PendingWrite): boolean {
  return write.attempts >= MAX_ATTEMPTS;
}

function describe(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Unbekannter Fehler';
}
